import { useContext, useEffect, useId, useMemo } from 'react'
import PropTypes from 'prop-types'
import * as HeroIconsSolid from '@heroicons/react/24/solid'
import { MapPinIcon } from '@heroicons/react/24/solid'
import { CalendarIcon, ClockIcon } from '@heroicons/react/24/outline'
import { MarkupContext } from '../../providers/Markup'
import ButtonEl from '../helpers/ctas/Button'
import MetaBadge from '../helpers/MetaBadge'
import { dataLayerPush } from '../../helpers/thirdparty'
import { formatEventDate } from '../../helpers/util'

const cardShadow = 'shadow-[0_4px_6px_-2px_rgba(0,0,0,0.05),0_10px_15px_-3px_rgba(0,0,0,0.10)]'

/** Icon colour: primary blue — non-text contrast vs card bg ≈ 3.6:1 (WCAG 1.4.11 ✓) */
const iconAccent = 'mt-0.5 h-4 w-4 shrink-0 text-sb-event-card-icon'

/**
 * Link colour: dark blue — contrast vs card bg ≈ 10.9:1 (WCAG AA + AAA ✓).
 * Primary blue (#367aab) on this bg is only 3.6:1 which fails AA for normal text,
 * so we use the card text colour and rely on the underline to identify links.
 */
const linkAccent =
  'text-sb-event-card-text underline decoration-2   hover:opacity-75 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--sb-focus-ring-color)'

function hasText(value) {
  if (value == null || typeof value !== 'string') {
    return false
  }
  return value.replace(/<[^>]*>/g, '').trim().length > 0
}

/** Mirrors the DynamicHeroIcon pattern from Button.jsx — resolves e.g. "Phone" → PhoneIcon */
function getHeroIcon(iconClass) {
  const icon = HeroIconsSolid[`${iconClass}Icon`]
  return icon || null
}

/** Bold segment before first colon for "NDS Members:" / "Non-members:" style lines */
function CostLine({ plainText }) {
  if (plainText == null || typeof plainText !== 'string') {
    return null
  }
  const text = plainText.replace(/<[^>]*>/g, '').trim()
  if (!text) {
    return null
  }
  const colon = text.indexOf(':')
  if (colon === -1) {
    return <p className="whitespace-pre-line text-sm text-sb-event-card-text/90">{text}</p>
  }
  const label = text.slice(0, colon + 1)
  const rest = text.slice(colon + 1).trimStart()
  return (
    <p className="whitespace-pre-line text-sm text-sb-event-card-text/90">
      <span className="font-normal text-sb-event-card-text">{label}</span>
      <span className="font-light text-sb-event-card-text">{rest.length > 0 ? ` ${rest}` : ''}</span>
    </p>
  )
}

function buildEventJsonLd({ eventTitle, eventStartDateIso, pageUrl, eventDateDisplay, deliveryMode }) {
  if (!eventTitle) {
    return null
  }

  const payload = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: eventTitle,
  }

  if (eventStartDateIso) {
    payload.startDate = eventStartDateIso
  }

  if (pageUrl) {
    payload.url = pageUrl
  }

  const modeLower = (deliveryMode || '').toLowerCase()
  if (modeLower.includes('hybrid')) {
    payload.eventAttendanceMode = 'https://schema.org/MixedEventAttendanceMode'
  } else if (modeLower.includes('online')) {
    payload.eventAttendanceMode = 'https://schema.org/OnlineEventAttendanceMode'
    payload.location = { '@type': 'VirtualLocation', url: pageUrl || undefined }
  } else if (modeLower.includes('person')) {
    payload.eventAttendanceMode = 'https://schema.org/OfflineEventAttendanceMode'
  }

  if (!payload.startDate && eventDateDisplay && !eventDateDisplay.includes('<')) {
    payload.description = `Event date: ${eventDateDisplay}`
  }

  return payload
}

/** Extracts numeric value from cost display strings e.g. "NDS Members: $310" → "310" */
const extractFee = (display) => {
  if (!display) return null
  const text = display.replace(/<[^>]*>/g, '').trim()
  const colonIdx = text.indexOf(':')
  const valuePart = colonIdx !== -1 ? text.slice(colonIdx + 1) : text
  const match = valuePart.match(/[\d.]+/)
  return match ? match[0] : null
}

function EventKeyDetails(props) {
  const { createMarkup } = useContext(MarkupContext)
  const jsonLdScriptId = `event-key-details-jsonld-${useId().replace(/:/g, '')}`

  const {
    eventTitle,
    primaryTag,
    deliveryMode,
    joinMethodText,
    joinMethodLink,
    locationMapLink,
    eventDateDisplay,
    showOtherDatesHtml,
    eventTimeDisplay,
    memberCostDisplay,
    nonMemberCostDisplay,
    registrationCta,
    alsoAvailableAs,
    eventStartDateIso,
    pageUrl,
    state,
  } = props

  const hasMeaningfulContent =
    hasText(eventTitle) ||
    hasText(primaryTag) ||
    hasText(deliveryMode) ||
    hasText(joinMethodText) ||
    (joinMethodLink && joinMethodLink.url) ||
    (locationMapLink && locationMapLink.url) ||
    hasText(eventDateDisplay) ||
    hasText(showOtherDatesHtml) ||
    hasText(eventTimeDisplay) ||
    hasText(memberCostDisplay) ||
    hasText(nonMemberCostDisplay) ||
    (registrationCta && registrationCta.link && (registrationCta.link.url || registrationCta.link.name)) ||
    (alsoAvailableAs && alsoAvailableAs.length > 0)

  const jsonLd = useMemo(
    () =>
      buildEventJsonLd({
        eventTitle,
        eventStartDateIso,
        pageUrl,
        eventDateDisplay,
        deliveryMode,
      }),
    [eventTitle, eventStartDateIso, pageUrl, eventDateDisplay, deliveryMode],
  )

  useEffect(() => {
    if (!jsonLd || typeof document === 'undefined') {
      return undefined
    }

    const existing = document.getElementById(jsonLdScriptId)
    if (existing) {
      existing.remove()
    }

    const script = document.createElement('script')
    script.id = jsonLdScriptId
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify(jsonLd)
    document.head.appendChild(script)

    return () => {
      const el = document.getElementById(jsonLdScriptId)
      if (el) {
        el.remove()
      }
    }
  }, [jsonLd, jsonLdScriptId])

  if (!hasMeaningfulContent) {
    return null
  }

  const showOtherMarkup = hasText(showOtherDatesHtml) ? createMarkup(showOtherDatesHtml) : null
  const joinHref = joinMethodLink?.url
  const mapHref = locationMapLink?.url
  const reg = registrationCta?.link
  const regUrl = reg?.url
  const regLabel = reg?.name?.trim()
  const registerText = regLabel ?? 'Register now'
  const showCost = hasText(memberCostDisplay) || hasText(nonMemberCostDisplay)

  const badgePrimaryText = hasText(primaryTag) ? primaryTag.replace(/<[^>]*>/g, '').trim() : undefined
  const badgeSecondaryText = hasText(deliveryMode) ? deliveryMode.replace(/<[^>]*>/g, '').trim() : undefined

  const handleRegisterClick = () => {
    dataLayerPush({
      event: 'registration_intent',
      event_title: eventTitle ?? null,
      event_date: formatEventDate(eventStartDateIso),
      event_location: joinMethodText?.replace(/<[^>]*>/g, '').trim() || locationMapLink?.name || null,
      member_fee: extractFee(memberCostDisplay),
      nonmember_fee: extractFee(nonMemberCostDisplay),
      event_delivery: badgeSecondaryText ?? null,
      event_state: state ?? null,
      event_type: badgePrimaryText ?? null,
      component_name: 'Event page',
    })
  }

  return (
    <>
      <aside
        data-type="eventKeyDetails"
        data-index={props.idx}
        aria-label="Event details"
        className="event-key-details w-full"
      >
        <div
          className={`flex flex-col gap-4 rounded-bl-3xl rounded-r-3xl bg-sb-event-card-bg p-6 ${cardShadow} text-sb-event-card-text mb-4`}
        >
          {hasText(eventTitle) && (
            <h2 className="text-lg font-light leading-snug text-sb-event-card-text lg:text-xl mb-0">{eventTitle}</h2>
          )}

          {(badgePrimaryText || badgeSecondaryText) && (
            <div className="flex flex-wrap gap-2">
              {/*
                tagType="event" → outer bg is --sb-meta-badge-event-theme (NDS orange #f2bc5b).
                secondaryTextColorClass overrides the default white to dark blue:
                  white on orange = 1.7:1 (fails WCAG AA)
                  dark blue on orange = 8.2:1 (passes WCAG AAA ✓)
              */}
              <MetaBadge
                tagType="event"
                primaryText={badgePrimaryText}
                secondaryText={badgeSecondaryText}
                secondaryTextColorClass="text-(--sb-meta-badge-primary-text-color)"
              />
            </div>
          )}

          <ul className="space-y-3 text-sb-event-card-text">
            {(hasText(joinMethodText) || joinHref) && (
              <li className="flex items-center gap-2">
                <MapPinIcon className={iconAccent} aria-hidden />
                <span>
                  {joinHref ? (
                    <a
                      href={joinHref}
                      className={`font-medium ${linkAccent}`}
                      {...(joinMethodLink?.target === '_blank' ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    >
                      {hasText(joinMethodText) ? joinMethodText : joinMethodLink?.name || 'Details'}
                    </a>
                  ) : (
                    <span className="font-light text-sm text-sb-event-card-text">{joinMethodText}</span>
                  )}
                </span>
              </li>
            )}

            {mapHref && (
              <li className="flex gap-2">
                <MapPinIcon className={iconAccent} aria-hidden />
                <a
                  href={mapHref}
                  className={`font-light text-sm ${linkAccent}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {locationMapLink?.name || 'View location on map'}
                </a>
              </li>
            )}

            {hasText(eventDateDisplay) && (
              <li className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <CalendarIcon className={iconAccent} aria-hidden />
                <span className="font-light text-sm text-sb-event-card-text">
                  {eventDateDisplay.replace(/<[^>]*>/g, '')}
                </span>
                {showOtherMarkup && (
                  <span
                    className="text-sm [&_a]:text-sb-event-card-text [&_a]:underline [&_a]:decoration-2 [&_a]: "
                    data-rte="true"
                    dangerouslySetInnerHTML={showOtherMarkup}
                  />
                )}
              </li>
            )}

            {!hasText(eventDateDisplay) && showOtherMarkup && (
              <li className="flex gap-2">
                <CalendarIcon className={iconAccent} aria-hidden />
                <span
                  className="text-sm [&_a]:text-sb-event-card-text [&_a]:underline"
                  data-rte="true"
                  dangerouslySetInnerHTML={showOtherMarkup}
                />
              </li>
            )}

            {hasText(eventTimeDisplay) && (
              <li className="flex items-center gap-2">
                <ClockIcon className={iconAccent} aria-hidden />
                <span className="font-light text-sm text-sb-event-card-text">
                  {eventTimeDisplay.replace(/<[^>]*>/g, '')}
                </span>
              </li>
            )}
          </ul>

          {showCost && (
            <div className="border-t border-sb-event-card-text/15 pt-4">
              <h3 className="text-lg font-light text-sb-event-card-text">Cost</h3>
              <div className="mt-2 space-y-2">
                {hasText(memberCostDisplay) && <CostLine plainText={memberCostDisplay} />}
                {hasText(nonMemberCostDisplay) && <CostLine plainText={nonMemberCostDisplay} />}
              </div>
            </div>
          )}

          {regUrl && (
            <div>
              <ButtonEl
                link={{ ...reg, name: registerText }}
                theme={(registrationCta.linkType || 'primary').toLowerCase()}
                icon={registrationCta.linkIconClass || 'ArrowRight'}
                position={registrationCta.linkPosition || 'After'}
                className="w-full justify-center event-key-details-register-button"
                onClick={handleRegisterClick}
              >
                {registerText}
              </ButtonEl>
            </div>
          )}

          {/* Locked pages: the server keeps the registration label but strips the URL
              (LockedContentTeaserFilter) — render a non-clickable button so visitors see
              that registration exists without being able to reach or inspect the link. */}
          {!regUrl && regLabel && (
            <div>
              <span
                aria-disabled="true"
                data-type="register-button-disabled"
                className="cta rounded-full font-semibold px-8 py-2.5 flex justify-center w-full border-2 bg-(--color-button-primary-bg) text-(--color-button-primary-text) border-(--color-button-primary-border) opacity-60 cursor-not-allowed select-none event-key-details-register-button"
              >
                {registerText}
              </span>
            </div>
          )}

          {alsoAvailableAs && alsoAvailableAs.length > 0 && (
            <div className="border-t border-sb-event-card-text/15 pt-4">
              <p className="text-lg font-light text-sb-event-card-text">Also available as:</p>
              <ul className="mt-2 space-y-2">
                {alsoAvailableAs.map((item, i) => {
                  const href = item.link?.url
                  const label = item.link?.name
                  if (!href || !label) {
                    return null
                  }
                  const IconComp = getHeroIcon(item.linkIconClass)
                  const external = item.link?.target === '_blank'
                  return (
                    <li key={`${href}-${i}`} className="flex items-center gap-2">
                      {IconComp && <IconComp className={`${iconAccent} text-sb-event-card-text`} aria-hidden />}
                      <a
                        href={href}
                        className={`font-light text-sm ${linkAccent}`}
                        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      >
                        {label}
                      </a>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </div>
      </aside>
    </>
  )
}

EventKeyDetails.propTypes = {
  idx: PropTypes.number,
  eventTitle: PropTypes.string,
  primaryTag: PropTypes.string,
  deliveryMode: PropTypes.string,
  joinMethodText: PropTypes.string,
  joinMethodLink: PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string,
    target: PropTypes.string,
  }),
  locationMapLink: PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string,
  }),
  eventDateDisplay: PropTypes.string,
  showOtherDatesHtml: PropTypes.string,
  eventTimeDisplay: PropTypes.string,
  memberCostDisplay: PropTypes.string,
  nonMemberCostDisplay: PropTypes.string,
  registrationCta: PropTypes.shape({
    link: PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
      target: PropTypes.string,
    }),
    linkType: PropTypes.string,
    linkIconClass: PropTypes.string,
  }),
  alsoAvailableAs: PropTypes.arrayOf(PropTypes.object),
  eventStartDateIso: PropTypes.string,
  pageUrl: PropTypes.string,
  state: PropTypes.string,
}

export default EventKeyDetails
